import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

export const fetchFilters = createAsyncThunk('filters/fetchFilters', async () => {
  try {
    const token = Cookies.get('token');

    const [
      filterResponse,
      userSavedFiltersResponse,
      machinesResponse,
      locationsResponse,
      machineGroupsResponse,
      maintenanceTypesResponse,
    ] = await Promise.all([
      fetch(process.env.REACT_APP_BASE_URL + 'notifications/relations', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'notifications/user-notification-preferences', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'machines', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'locations', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'machine-groups', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
      fetch(process.env.REACT_APP_BASE_URL + 'maintenance-types', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    ]);

    const filters = await filterResponse.json();
    const filtersUser = await userSavedFiltersResponse.json();
    const allMachines = await machinesResponse.json();
    const allLocations = await locationsResponse.json();
    const allMachineGroups = await machineGroupsResponse.json();
    const allMaintenanceTypes = await maintenanceTypesResponse.json();

    // Check if user has notification settings configured
    const isNotificationEmpty =
      filtersUser.machines.length === 0 &&
      filtersUser.machineGroups.length === 0 &&
      filtersUser.locations.length === 0 &&
      filtersUser.maintenanceTypes.length === 0;

    const notificationSelectedFilters = isNotificationEmpty
      ? null
      : {
          machines: filtersUser.machines.map((machine) => +machine.machine.id),
          machineGroups: filtersUser.machineGroups.map((group) => +group.machine_group.id),
          locations: filtersUser.locations.map((location) => +location.location.id),
          maintenanceTypes: filtersUser.maintenanceTypes.map((type) => +type.maintenance_type.id),
        };

    // Extract arrays from the API response structures
    const machinesArray = Array.isArray(allMachines)
      ? allMachines
      : allMachines?.activeMachines
      ? allMachines.activeMachines
      : [];
    const locationsArray = Array.isArray(allLocations) ? allLocations : [];
    const machineGroupsArray = Array.isArray(allMachineGroups)
      ? allMachineGroups
      : allMachineGroups?.activeMachineGroups
      ? allMachineGroups.activeMachineGroups
      : [];
    const maintenanceTypesArray = Array.isArray(allMaintenanceTypes) ? allMaintenanceTypes : [];

    return {
      filters: {
        machines: machinesArray.map((machine) => ({
          id: +machine.id,
          name: `${machine.name} ${machine.number}`,
        })),
        machineGroups: machineGroupsArray.map((group) => ({
          id: +group.id,
          name: group.name,
        })),
        locations: locationsArray.map((location) => ({
          id: +location.id,
          name: location.name,
        })),
        maintenanceTypes: maintenanceTypesArray.map((type) => ({
          id: +type.id,
          name: type.name,
        })),
      },
      selectedFilters: {
        // If no notification settings, show all items selected
        // If has notification settings, use notification settings as selected filters
        machines: isNotificationEmpty
          ? machinesArray.map((machine) => +machine.id)
          : notificationSelectedFilters.machines,
        machineGroups: isNotificationEmpty
          ? machineGroupsArray.map((group) => +group.id)
          : notificationSelectedFilters.machineGroups,
        locations: isNotificationEmpty
          ? locationsArray.map((location) => +location.id)
          : notificationSelectedFilters.locations,
        maintenanceTypes: isNotificationEmpty
          ? maintenanceTypesArray.map((type) => +type.id)
          : notificationSelectedFilters.maintenanceTypes,
      },
      notificationSettings: notificationSelectedFilters, // Store notification settings for comparison
      hasNotificationSettings: !isNotificationEmpty,
      rawMachines: filters.machines.map((machine) => ({
        ...machine,
        locationIds: machine.locationIds.map(Number),
        machineGroupIds: machine.machineGroupIds.map(Number),
        machineIds: machine.machineIds.map(Number),
        maintenanceTypeIds: machine.maintenanceTypeIds.map(Number),
      })),
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
});

const initialState = {
  filters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  selectedFilters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  activeFilters: {
    machines: [],
    locations: [],
    machineGroups: [],
    maintenanceTypes: [],
  },
  rawMachines: [],
  filteredMachines: [],
  isLoading: false,
  error: null,
  activeCalendarView: 0,
  notificationSettings: null, // Store user's notification settings
  hasNotificationSettings: false, // Flag to know if user has configured notifications
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    resetFilters: () => JSON.parse(JSON.stringify(initialState)),
    filterMachines: (state) => {
      // Step 1: Filter machines based on location, machine groups, and maintenance types
      // but NOT on selected machines yet
      // Uses AND logic: machines must match ALL selected criteria within each filter type
      state.filteredMachines = state.rawMachines
        .filter(
          (machine) =>
            // If no locations selected, show all machines; otherwise machine must have ALL selected locations
            state.selectedFilters.locations.length === 0 ||
            state.selectedFilters.locations.every((location) =>
              machine.locationIds.includes(location)
            )
        )
        .filter(
          (machine) =>
            // If no machine groups selected, show all machines; otherwise machine must have ALL selected groups
            state.selectedFilters.machineGroups.length === 0 ||
            state.selectedFilters.machineGroups.every((group) =>
              machine.machineGroupIds.includes(group)
            )
        )
        .filter(
          (machine) =>
            // If no maintenance types selected, show all machines; otherwise machine must have ALL selected types
            state.selectedFilters.maintenanceTypes.length === 0 ||
            state.selectedFilters.maintenanceTypes.every((type) =>
              machine.maintenanceTypeIds.includes(type)
            )
        );

      // Step 2: Set available machines based on the filtering above
      state.activeFilters.machines = [
        ...new Set(state.filteredMachines.map((machine) => +machine.id)),
      ];

      // Step 3: Now filter by selected machines
      state.filteredMachines = state.filteredMachines.filter((machine) =>
        state.selectedFilters.machines.includes(+machine.id)
      );

      // Step 4: Update available options for other filters based on final filtered machines
      state.activeFilters.machineGroups = [
        ...new Set(state.filteredMachines.flatMap((machine) => machine.machineGroupIds)),
      ];
      state.activeFilters.locations = [
        ...new Set(state.filteredMachines.flatMap((machine) => machine.locationIds)),
      ];
      state.activeFilters.maintenanceTypes = [
        ...new Set(state.filteredMachines.flatMap((machine) => machine.maintenanceTypeIds)),
      ];
    },
    toggleMachine: (state, action) => {
      if (state.selectedFilters.machines.includes(action.payload)) {
        state.selectedFilters.machines = state.selectedFilters.machines.filter(
          (id) => id !== action.payload
        );
      } else {
        state.selectedFilters.machines = [...state.selectedFilters.machines, action.payload];
      }
    },
    toggleGroup: (state, action) => {
      if (state.selectedFilters.machineGroups.includes(action.payload)) {
        state.selectedFilters.machineGroups = state.selectedFilters.machineGroups.filter(
          (id) => id !== action.payload
        );
      } else {
        state.selectedFilters.machineGroups = [
          ...state.selectedFilters.machineGroups,
          action.payload,
        ];
      }
    },
    toggleLocation: (state, action) => {
      if (state.selectedFilters.locations.includes(action.payload)) {
        state.selectedFilters.locations = state.selectedFilters.locations.filter(
          (id) => id !== action.payload
        );
      } else {
        state.selectedFilters.locations = [...state.selectedFilters.locations, action.payload];
      }
    },
    toggleType: (state, action) => {
      if (state.selectedFilters.maintenanceTypes.includes(action.payload)) {
        state.selectedFilters.maintenanceTypes = state.selectedFilters.maintenanceTypes.filter(
          (id) => id !== action.payload
        );
      } else {
        state.selectedFilters.maintenanceTypes = [
          ...state.selectedFilters.maintenanceTypes,
          action.payload,
        ];
      }
    },
    setActiveCalendarView: (state, action) => {
      state.activeCalendarView = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilters.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchFilters.fulfilled, (state, action) => {
      state.isLoading = false;
      state.filters = action.payload.filters;
      state.rawMachines = action.payload.rawMachines;
      state.selectedFilters = action.payload.selectedFilters;
      state.notificationSettings = action.payload.notificationSettings;
      state.hasNotificationSettings = action.payload.hasNotificationSettings;

      // Initialize activeFilters with all available options
      state.activeFilters = {
        machines: action.payload.filters.machines.map((machine) => machine.id),
        machineGroups: action.payload.filters.machineGroups.map((group) => group.id),
        locations: action.payload.filters.locations.map((location) => location.id),
        maintenanceTypes: action.payload.filters.maintenanceTypes.map((type) => type.id),
      };

      state.error = null;
    });
    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
  },
});

export const {
  resetFilters,
  filterMachines,
  toggleGroup,
  toggleLocation,
  toggleMachine,
  toggleType,
  toggleEnabled,
  setActiveCalendarView,
} = filtersSlice.actions;

export default filtersSlice.reducer;

export const selectFilters = (state) => state.filters.filters;
export const selectActiveCalendarView = (state) => state.filters.activeCalendarView;
